import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
import externalLinkFill from '@iconify/icons-eva/external-link-fill';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  Link,
  Button
} from '@mui/material';
// contexts
import { EventContext } from '../../contexts/Event';
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';
// services
import { activateEvent, inactivateEvent } from '../../services/Event';
// helpers
import { getEventDomain } from '../../helpers/Util';
//
import { MHidden } from '../../components/@material-extend';
import AccountPopover from './AccountPopover';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha(theme.palette.background.default, 0.72),
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH + 1}px)`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5)
  }
}));

// ----------------------------------------------------------------------

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func
};

export default function DashboardNavbar({ onOpenSidebar }) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event, setEvent } = useContext(EventContext);

  const handleStatus = async () => {
    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };

      if (event.active) {
        await inactivateEvent({ headers, id: event._id });
        setEvent((event) => ({ ...event, active: false }));
      } else {
        await activateEvent({ headers, id: event._id });
        setEvent((event) => ({ ...event, active: true }));
      }
    } catch (err) {
      alert('An error occurred — Please, try again!');
      console.error(err);
    }
  };

  return (
    <RootStyle>
      <ToolbarStyle>
        <MHidden width="lgUp">
          <IconButton onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>

        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
          <Box>
            {event?.name && (
              <Button
                startIcon={<Icon icon={externalLinkFill} width="24" />}
                href={`//${event.route?.subdomain}.${getEventDomain()}`}
                target="_blank"
                component={Link}
                disabled={
                  !(event.active && event.route?.subdomain && event.calendar?.schedule?.length)
                }
              >
                <Typography
                  component="h1"
                  variant="h4"
                  color="black"
                  noWrap
                  sx={{ maxWidth: { xs: '80px', md: '100%' } }}
                >
                  {event.name}
                </Typography>
              </Button>
            )}
          </Box>
          {event?.name && (
            <>
              <Chip
                label={event.active ? 'active' : 'inactive'}
                color={event.active ? 'success' : 'error'}
                size="small"
                sx={{ mx: 1 }}
              />
              <FormControlLabel
                checked={event.active}
                control={<Switch onChange={handleStatus} />}
                sx={{ color: 'black' }}
              />
            </>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
